import { Spinner, Text, XStack, YStack } from "tamagui";

import { colors } from "@styles/colors";
import PointsRedeemRow from "@components/points/PointsRedeemRow";
import PointsSavingBanner from "@components/points/PointsSavingBanner";
import { useOrder } from "@utils/store/cart/useOrder";
import { v2 } from "../skin";
import { useCartPointsRedeem } from "../utils/useCartPointsRedeem";

// 這條流程的欄位是「一行值 + 右邊動作、沒有圖示」，點數跟著同一種身段；
// 自己一張卡之後，兩列之間補一條與其他白卡同款的細分隔線。
const SKIN = { ink: v2.title, sub: v2.sub, line: v2.divider, accent: v2.primary };

/**
 * 購物車的「點數折抵」。折抵順序是優惠券 → 存氣點數 → 帳戶點數（見 calcPointsRedeem），
 * 所以這一區放在優惠券下面：由上而下就是折抵發生的順序。
 *
 * 帳戶完全沒點數時整區隱藏（不做「餘額 0 點」的空欄位）。
 * intentOnly：瓦斯行開啟隱藏金額時，這張單的金額要接單後才有，折抵算不出數字 ——
 * 改成只收「要不要折」，由瓦斯行定價時套用。
 * 版面不自帶卡片：它落在購物車那張合併白卡裡，與優惠券、支付方式是同一張卡的三段。
 */
const PointsRedeemSection = ({ intentOnly = false }: { intentOnly?: boolean }) => {
  const { pointsRedeem, setPointsRedeem, pointsIntent, setPointsIntent } = useOrder();
  const { result, balance, loading, ready, empty } = useCartPointsRedeem();

  if (empty) return null;
  if (loading) {
    return (
      <XStack ai="center" gap={8} py={10}>
        <Spinner size="small" color={colors.primary} />
        <Text fontSize={12} color={v2.sub}>
          讀取點數…
        </Text>
      </XStack>
    );
  }

  return (
    <YStack>
      {balance.storedGas > 0 && (
        <PointsRedeemRow
          kind="stored_gas"
          balance={balance.storedGas}
          used={result.storedGas}
          max={result.maxStoredGas}
          ready={ready}
          onChange={(storedGas) => setPointsRedeem({ ...pointsRedeem, storedGas })}
          skin={SKIN}
          isLast={balance.account <= 0}
          intent={
            intentOnly
              ? { on: pointsIntent.storedGas, onToggle: (on) => setPointsIntent({ ...pointsIntent, storedGas: on }) }
              : undefined
          }
        />
      )}
      {balance.account > 0 && (
        <PointsRedeemRow
          kind="account"
          balance={balance.account}
          used={result.account}
          max={result.maxAccount}
          ready={ready}
          onChange={(account) => setPointsRedeem({ ...pointsRedeem, account })}
          skin={SKIN}
          isLast
          intent={
            intentOnly
              ? { on: pointsIntent.account, onToggle: (on) => setPointsIntent({ ...pointsIntent, account: on }) }
              : undefined
          }
        />
      )}

      {/* 折了多少要當場說一次：折抵金額雖然也出現在訂單摘要，但那要再往下捲一段，
          在這裡看不到回饋的話，使用者不確定剛剛那一下有沒有生效。
          沒折抵時什麼都不放 —— 原本那句「點數由這家瓦斯行認列…」是整段最占高度、
          又最少人會讀的一行，兩種點數的用途已經寫在各自那列的「只折瓦斯／全單可折」。 */}
      {!intentOnly && result.discount > 0 && (
        <YStack mt={12}>
          <PointsSavingBanner discount={result.discount} />
        </YStack>
      )}
    </YStack>
  );
};

export default PointsRedeemSection;
